/**
 * WhatsApp-style "Conversation info" right-side slide-in panel.
 * Replaces the old compact dropdown attached to the chat header's
 * three-dot button. Contains all the prior menu actions plus a
 * dedicated "Linked record" section showing the conversation's
 * `saas_record` (whatever module the user linked — Contact / Ticket
 * / Account / Order — i.e. the record sent messages get attached to
 * as notes / activities in the CRM).
 *
 * `saas_record` is fetched directly from the inbox Firestore doc on
 * panel open because the Conversation in-memory shape now sources
 * `crmRecord` from the newer `saas_contact` field instead, and those
 * two are different stamps with different lifecycles.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Copy,
  Pin,
  PinOff,
  Pencil,
  Calendar,
  Bell,
  Timer,
  ArrowLeft,
  ExternalLink,
  Loader2,
  MessageSquare,
  Mail,
  Radio,
  MessageCircle,
  Link2,
  Check,
} from 'lucide-react';
import { doc, getDoc } from '@/lib/firebase/firestore';
import { db } from '@/config/firebase';
import { useAppStore } from '@/stores/app-store';
import { useModuleStore } from '@/stores/module-store';
import { getInboxConvDocPath, useConversationStore, normalizeConvId } from '@/stores/conversation-store';
import { useSettingsStore } from '@/stores/settings-store';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { InstagramIcon } from '@/components/icons/instagram-icon';
import { TelegramIcon } from '@/components/icons/telegram-icon';
import type { Conversation, Channel } from '@/types/messaging';
import { cn } from '@/lib/utils';

const CHANNEL_LABELS: Record<Channel, string> = {
  SMS: 'SMS',
  MMS: 'MMS',
  WHATSAPP: 'WhatsApp',
  VOICE: 'Voice',
  EMAIL: 'Email',
  INTERCOM: 'Intercom',
  INSTAGRAM: 'Instagram',
  TELEGRAM: 'Telegram',
};

const CHANNEL_ICONS: Record<Channel, React.ComponentType<{ className?: string }>> = {
  SMS: MessageSquare,
  MMS: MessageSquare,
  WHATSAPP: WhatsAppIcon,
  VOICE: Radio,
  EMAIL: Mail,
  INTERCOM: MessageCircle,
  INSTAGRAM: InstagramIcon,
  TELEGRAM: TelegramIcon,
};

const CHANNEL_ICON_COLORS: Record<Channel, string> = {
  SMS: 'text-blue-600 dark:text-blue-400',
  MMS: 'text-blue-600 dark:text-blue-400',
  WHATSAPP: 'text-green-600 dark:text-green-400',
  VOICE: 'text-purple-600 dark:text-purple-400',
  EMAIL: 'text-slate-600 dark:text-slate-400',
  INTERCOM: 'text-indigo-600 dark:text-indigo-400',
  INSTAGRAM: 'text-pink-600 dark:text-pink-400',
  TELEGRAM: 'text-sky-600 dark:text-sky-400',
};

export interface ConversationInfoPanelProps {
  open: boolean;
  onClose: () => void;
  conversation: Conversation;
  senderNumber?: string;
  contactNumber?: string;
  hasReminder: boolean;
  onCopyPhone: () => void;
  onTogglePin: () => void;
  onRename: () => void;
  onJumpToDate: () => void;
  onReminder: () => void;
  onCloseConversation: () => void;
}

type SaasRecord = {
  appCode: string;
  entityId: string;
  module: string;
  recordName?: string;
};

export function ConversationInfoPanel({
  open,
  onClose,
  conversation,
  senderNumber,
  contactNumber,
  hasReminder,
  onCopyPhone,
  onTogglePin,
  onRename,
  onJumpToDate,
  onReminder,
  onCloseConversation,
}: ConversationInfoPanelProps) {
  const saasModule = useModuleStore((s) => s.saasModule);
  const extensionName = useAppStore((s) => s.extensionName);
  const saasName = saasModule?.name || 'CRM';
  // Read pin state from the persisted personal setting rather than the
  // conversation object — see the note in conversation-item.tsx.
  const isPinned = useSettingsStore((s) => {
    const target = normalizeConvId(conversation.id);
    return s.pinnedConversationIds.some((id) => normalizeConvId(id) === target);
  });

  const [saasRecord, setSaasRecord] = useState<SaasRecord | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(false);

  // "Link by ID" — direct manual entry of module + entity id. Lets the
  // user attach the conversation to a specific CRM record even when no
  // recipient picker / widget context is available (the typical inbox
  // case). Calls the same CF the post-send write uses.
  const updateExistingRecordInConversation = useConversationStore((s) => s.updateExistingRecordInConversation);
  const activeSender = useConversationStore((s) => s.activeSender);
  const supportedModules = useMemo(
    () => saasModule?.getSupportedModules?.() ?? [],
    [saasModule],
  );
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkTab, setLinkTab] = useState<'url' | 'search'>('url');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkSearchModule, setLinkSearchModule] = useState<string>('');
  const [linkSearch, setLinkSearch] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState<Array<{
    id: string;
    module: string;
    displayName: string;
  }>>([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  // After "Fetch" / search-pick, store the resolved record so the user
  // can confirm before we write to Firestore.
  const [linkPreview, setLinkPreview] = useState<{
    module: string;
    entityId: string;
    recordName?: string;
    data?: Record<string, unknown>;
  } | null>(null);

  // Esc-to-close + body scroll lock + reset link form on close.
  useEffect(() => {
    if (!open) {
      setLinkOpen(false);
      setLinkTab('url');
      setLinkUrl('');
      setLinkSearchModule('');
      setLinkSearch('');
      setLinkSearchResults([]);
      setLinkPreview(null);
      setLinkError(null);
      setLinkLoading(false);
      setLinkSaving(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Fetch saas_record from the inbox doc when the panel opens. Read
  // priority mirrors the inbox-row reader: `saas_record_{ext}` (per-
  // extension override) → `saas_record` (generic). Read-only — no
  // writes. Falls back to nothing on error / missing doc.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSaasRecord(null);
    setLoadingRecord(true);
    (async () => {
      try {
        const path = getInboxConvDocPath(conversation.id);
        if (!path) return;
        const snap = await getDoc(doc(db, path));
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as Record<string, unknown>;
        const extKey = extensionName ? `saas_record_${extensionName}` : null;
        const raw = (extKey ? data[extKey] : undefined) || data.saas_record;
        if (raw && typeof raw === 'object') {
          const r = raw as Record<string, unknown>;
          if (r.entityId) {
            setSaasRecord({
              appCode: typeof r.appCode === 'string' ? r.appCode : (extensionName || ''),
              entityId: String(r.entityId),
              module: typeof r.module === 'string' ? r.module : '',
              recordName: typeof r.recordName === 'string' ? r.recordName : undefined,
            });
          }
        }
      } catch (e) {
        console.warn('[ConversationInfoPanel] saas_record fetch failed:', e);
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, conversation.id, extensionName]);

  if (!open) return null;

  const channelLabel = CHANNEL_LABELS[conversation.channel] ?? conversation.channel;
  const ChannelIcon = CHANNEL_ICONS[conversation.channel] || MessageSquare;
  const channelIconColor = CHANNEL_ICON_COLORS[conversation.channel] || 'text-muted-foreground';
  const phoneOrContact = conversation.contactPhone || '';

  // Display-name resolution — mirrors conversation-item / chat-thread:
  //   1. subject (= contactName when it's a real name, not just the phone)
  //   2. crmRecordName (sourced from saas_contact)
  //   3. contactName (even if it's the phone-shaped fallback)
  //   4. contactPhone
  //   5. 'Unknown'
  const hasRealSubject = !!conversation.contactName
    && conversation.contactName !== phoneOrContact
    && conversation.contactName !== `+${phoneOrContact}`;
  const displayName =
    (hasRealSubject ? conversation.contactName : null)
    || conversation.crmRecordName
    || conversation.contactName
    || phoneOrContact
    || 'Unknown';
  const recordWebUrl = saasRecord && saasModule && 'getRecordWebURL' in saasModule
    ? (saasModule as unknown as { getRecordWebURL?: (m: string, id: string) => string | null }).getRecordWebURL?.(saasRecord.module, saasRecord.entityId) ?? null
    : null;

  /**
   * Best-effort module detection from a record URL. Tries each
   * supported module's `getRecordWebURL(module, entityId)` pattern and
   * picks the one whose path segments overlap most with the input URL.
   * Falls back to scanning URL path segments for module-name matches
   * (singular and plural forms) — covers Pipedrive `/person/`,
   * Salesforce `/lightning/r/Contact/`, Zoho `/tab/Contacts/`, etc.
   */
  const detectModuleFromUrl = (url: string, entityId: string): string | null => {
    if (!saasModule || !url) return null;
    const norm = url.toLowerCase();
    const sm = saasModule as unknown as { getRecordWebURL?: (m: string, id: string) => string | null };
    // Pass 1: exact pattern match.
    for (const m of supportedModules) {
      try {
        const built = sm.getRecordWebURL?.(m.value, entityId);
        if (built && norm.includes(built.toLowerCase())) return m.value;
      } catch { /* try next */ }
    }
    // Pass 2: path-segment heuristic.
    const segs = norm.replace(/^https?:\/\//, '').split(/[/?#]/).filter(Boolean);
    for (const m of supportedModules) {
      const v = m.value.toLowerCase();
      const singular = v.endsWith('s') ? v.slice(0, -1) : v;
      if (segs.includes(v) || segs.includes(singular)) return m.value;
    }
    return null;
  };

  const handleLinkFetch = async () => {
    setLinkError(null);
    setLinkPreview(null);
    const url = linkUrl.trim();
    if (!url || !saasModule) {
      setLinkError('Paste a record URL to continue.');
      return;
    }
    const entityId = saasModule.parseEntityIdFromURL?.(url) ?? null;
    if (!entityId) {
      setLinkError(`Couldn't parse a record id from this URL. Use the record's URL in ${saasName}.`);
      return;
    }
    const moduleValue = detectModuleFromUrl(url, entityId);
    if (!moduleValue) {
      setLinkError(`Couldn't detect the ${saasName} module from this URL.`);
      return;
    }
    setLinkLoading(true);
    try {
      const rec = await (saasModule as unknown as {
        fetchRecord?: (m: string, id: string) => Promise<{ id: string; displayName?: string; data?: Record<string, unknown> } | null>;
      }).fetchRecord?.(moduleValue, entityId);
      if (!rec) {
        setLinkError('Record not found.');
        return;
      }
      setLinkPreview({
        module: moduleValue,
        entityId,
        recordName: rec.displayName || undefined,
        data: rec.data,
      });
    } catch (err) {
      console.warn('[InfoPanel] fetchRecord failed:', err);
      setLinkError(err instanceof Error ? err.message : 'Failed to fetch record.');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleLinkSearch = async () => {
    setLinkError(null);
    setLinkSearchResults([]);
    const q = linkSearch.trim();
    if (!q || !saasModule) {
      setLinkError('Type a name to search.');
      return;
    }
    const moduleToSearch = linkSearchModule || supportedModules[0]?.value || '';
    if (!moduleToSearch) {
      setLinkError(`No ${saasName} modules available to search.`);
      return;
    }
    setLinkLoading(true);
    try {
      // Name-based search scoped to the user-picked module. The
      // adapter interprets field='name' as a name-field match
      // (Zoho → ?word=; Pipedrive → /itemSearch?fields=name;
      // Salesforce → FIND … IN NAME FIELDS; etc.).
      const results = await (saasModule as unknown as {
        searchAcrossModules?: (q: string, field?: string, options?: { modules?: string[] }) => Promise<Array<{ id: string; module: string; displayName?: string }>>;
      }).searchAcrossModules?.(q, 'name', { modules: [moduleToSearch] });
      const list = (results || [])
        .filter((r) => !!r.id && !!r.module)
        .map((r) => ({ id: r.id, module: r.module, displayName: r.displayName || r.id }));
      setLinkSearchResults(list);
      if (list.length === 0) setLinkError('No matching records found.');
    } catch (err) {
      console.warn('[InfoPanel] search failed:', err);
      setLinkError(err instanceof Error ? err.message : 'Search failed.');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleSearchResultPick = (r: { id: string; module: string; displayName: string }) => {
    setLinkError(null);
    setLinkPreview({
      module: r.module,
      entityId: r.id,
      recordName: r.displayName || undefined,
    });
  };

  const handleLinkConfirm = async () => {
    if (!linkPreview) return;
    setLinkSaving(true);
    setLinkError(null);
    try {
      await updateExistingRecordInConversation(
        conversation.id,
        linkPreview.module,
        linkPreview.entityId,
        linkPreview.recordName,
      );
      // Optimistic local update so the card reflects the new link
      // without waiting for the Firestore realtime push to land.
      setSaasRecord({
        appCode: extensionName || '',
        entityId: linkPreview.entityId,
        module: linkPreview.module,
        recordName: linkPreview.recordName,
      });
      setLinkOpen(false);
      setLinkUrl('');
      setLinkPreview(null);
    } catch (err) {
      console.error('[InfoPanel] link confirm failed:', err);
      setLinkError(err instanceof Error ? err.message : 'Failed to update linked record.');
    } finally {
      setLinkSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px] animate-in fade-in-0"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-label="Conversation info"
        className="absolute right-0 top-0 h-full w-full sm:w-[400px] max-w-full flex flex-col bg-background border-l border-border shadow-xl animate-slide-in-right"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3 border-b border-border/60">
          <h3 className="text-base font-semibold text-foreground truncate">Conversation info</h3>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-sm p-1 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Contact identity block */}
          <div className="px-5 py-5 border-b border-border/60 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-muted flex items-center justify-center text-2xl font-medium text-foreground/70 mb-3">
              {(displayName || '?').charAt(0).toUpperCase()}
            </div>
            <div className="text-base font-medium text-foreground truncate">
              {displayName}
            </div>
            {phoneOrContact && displayName !== phoneOrContact && displayName !== `+${phoneOrContact}` && (
              <div className="text-xs text-muted-foreground font-mono mt-0.5">{phoneOrContact}</div>
            )}
          </div>

          {/* Linked saas_record — what messages sent in this thread are
              attached to in the CRM (notes / activities). Distinct from
              the chat-row display contact (saas_contact).
              When the link-by-URL/Search dialog is open we hide the
              existing record card so the dialog clearly *replaces*
              instead of looking like it's appending another record. */}
          <div className="px-5 py-4 border-b border-border/60">
            {!linkOpen && (
              <>
                <div className="text-[11px] text-primary uppercase tracking-wide text-muted-foreground/80 font-medium mb-1">
                  <b>Linked {saasName} record</b>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Messages in this conversation are added to this {saasName} record.
                </p>
              </>
            )}
            {linkOpen ? null : loadingRecord ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading…
              </div>
            ) : saasRecord ? (
              <div className="relative flex items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2">
                <div className="min-w-0">
                  {recordWebUrl ? (
                    <a
                      href={recordWebUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open in ${saasName}`}
                      className="text-sm text-primary hover:underline truncate inline-flex items-center gap-1 max-w-full"
                    >
                      <span className="truncate">{saasRecord.recordName || saasRecord.entityId}</span>
                      <ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
                    </a>
                  ) : (
                    <div className="text-sm text-foreground truncate">
                      {saasRecord.recordName || saasRecord.entityId}
                    </div>
                  )}
                  {saasRecord.module && (
                    <div className="text-[11px] text-muted-foreground capitalize">
                      {saasRecord.module}
                    </div>
                  )}
                </div>
                {saasModule?.icon && (
                  <img
                    src={saasModule.icon}
                    alt={saasName}
                    title={saasName}
                    className="absolute bottom-1.5 right-1.5 h-4 w-4 object-contain opacity-70 pointer-events-none"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
              </div>
            ) : (
              // Empty state — prominent call to action so the user knows
              // exactly what to do next. Replaces the small "Link …"
              // trigger below when no record is linked.
              <div className="flex flex-col items-center text-center rounded-md border border-dashed border-border/70 bg-muted/20 px-4 py-5">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <Link2 className="h-4 w-4 text-primary" />
                </div>
                <div className="text-sm font-medium text-foreground">
                  No {saasName} record linked yet
                </div>
                <p className="text-xs text-muted-foreground mt-1 max-w-[260px]">
                  Link a record so messages sent here are attached to it as notes in {saasName}.
                </p>
                <button
                  type="button"
                  onClick={() => setLinkOpen(true)}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                >
                  <Link2 className="h-3.5 w-3.5" />
                  Link a {saasName} record
                </button>
              </div>
            )}

            {/* Link-by-URL/Search trigger — only shown when a record
                already exists; the empty-state CTA above covers the
                no-record case. */}
            <div className={cn(saasRecord ? 'mt-3' : '')}>
              {!linkOpen && saasRecord ? (
                <button
                  type="button"
                  onClick={() => setLinkOpen(true)}
                  className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Link2 className="h-3 w-3" />
                  Change linked {saasName} record
                </button>
              ) : !linkOpen ? null : (
                <div className="rounded-md border border-border/60 bg-background overflow-hidden">
                  {/* Dialog title bar — distinct header so it clearly
                      reads as a titled form, not just another inline
                      field group. */}
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border/60 bg-muted/30">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Link2 className="h-3.5 w-3.5 text-primary shrink-0" />
                      <div className="text-sm font-semibold text-foreground truncate">
                        {linkPreview
                          ? `Confirm new ${saasName} link`
                          : saasRecord
                            ? `Change linked ${saasName} record`
                            : `Link a ${saasName} record`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setLinkOpen(false);
                        setLinkUrl('');
                        setLinkSearch('');
                        setLinkSearchResults([]);
                        setLinkPreview(null);
                        setLinkError(null);
                      }}
                      disabled={linkLoading || linkSaving}
                      className="shrink-0 rounded-sm p-1 opacity-70 hover:opacity-100 transition-opacity"
                      aria-label="Close link dialog"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="p-3 space-y-2">
                    {!linkPreview ? (
                      <>
                        {/* Subtab switch */}
                        <div className="flex items-center gap-1 mb-1">
                          <button
                            type="button"
                            onClick={() => { setLinkTab('url'); setLinkError(null); }}
                            className={cn(
                              'text-[11px] px-2 py-1 rounded transition-colors',
                              linkTab === 'url'
                                ? 'bg-primary/10 text-foreground border border-primary/30'
                                : 'text-muted-foreground hover:text-foreground',
                            )}
                          >
                            Paste URL
                          </button>
                          <button
                            type="button"
                            onClick={() => { setLinkTab('search'); setLinkError(null); }}
                            className={cn(
                              'text-[11px] px-2 py-1 rounded transition-colors',
                              linkTab === 'search'
                                ? 'bg-primary/10 text-foreground border border-primary/30'
                                : 'text-muted-foreground hover:text-foreground',
                            )}
                          >
                            Search
                          </button>
                        </div>

                        {linkTab === 'url' ? (
                          <>
                            <label className="text-[11px] text-muted-foreground">
                              Paste the {saasName} record URL
                            </label>
                            <input
                              type="url"
                              value={linkUrl}
                              onChange={(e) => setLinkUrl(e.target.value)}
                              placeholder={`https://…/${supportedModules[0]?.value.toLowerCase() || 'record'}/12345`}
                              className="w-full h-8 px-2 rounded border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary/40"
                              disabled={linkLoading}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLinkFetch(); } }}
                              autoFocus
                            />
                            {linkError && (
                              <p className="text-[11px] text-destructive">{linkError}</p>
                            )}
                            <div className="flex items-center justify-end gap-1.5 pt-1">
                              <button
                                type="button"
                                onClick={() => { setLinkOpen(false); setLinkUrl(''); setLinkError(null); }}
                                className="text-[11px] px-2 py-1 rounded hover:bg-muted text-muted-foreground"
                                disabled={linkLoading}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleLinkFetch}
                                disabled={linkLoading || !linkUrl.trim()}
                                className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                              >
                                {linkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                Fetch
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <label className="text-[11px] text-muted-foreground">
                              Search {saasName} records by name
                            </label>
                            <select
                              value={linkSearchModule || supportedModules[0]?.value || ''}
                              onChange={(e) => setLinkSearchModule(e.target.value)}
                              disabled={linkLoading || supportedModules.length === 0}
                              className="w-full h-8 px-2 rounded border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary/40"
                            >
                              {supportedModules.map((m) => (
                                <option key={m.value} value={m.value}>
                                  {m.label}
                                </option>
                              ))}
                            </select>
                            <div className="flex gap-1.5">
                              <input
                                type="search"
                                value={linkSearch}
                                onChange={(e) => setLinkSearch(e.target.value)}
                                placeholder="e.g. John Doe"
                                className="flex-1 h-8 px-2 rounded border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary/40"
                                disabled={linkLoading}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLinkSearch(); } }}
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={handleLinkSearch}
                                disabled={linkLoading || !linkSearch.trim()}
                                className="inline-flex items-center gap-1 text-[11px] px-2.5 h-8 rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                              >
                                {linkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                Search
                              </button>
                            </div>
                            {linkError && (
                              <p className="text-[11px] text-destructive">{linkError}</p>
                            )}
                            {linkSearchResults.length > 0 && (
                              <ul className="max-h-48 overflow-y-auto rounded border border-border/60 divide-y divide-border/40">
                                {linkSearchResults.map((r) => (
                                  <li key={`${r.module}_${r.id}`}>
                                    <button
                                      type="button"
                                      onClick={() => handleSearchResultPick(r)}
                                      className="w-full text-left px-2.5 py-1.5 hover:bg-muted transition-colors"
                                    >
                                      <div className="text-xs text-foreground truncate">{r.displayName || r.id}</div>
                                      <div className="text-[10px] text-muted-foreground capitalize">{r.module} · id {r.id}</div>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div className="flex items-center justify-end gap-1.5 pt-1">
                              <button
                                type="button"
                                onClick={() => { setLinkOpen(false); setLinkSearch(''); setLinkSearchResults([]); setLinkError(null); }}
                                className="text-[11px] px-2 py-1 rounded hover:bg-muted text-muted-foreground"
                                disabled={linkLoading}
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-[11px] text-muted-foreground">
                          Confirm linking this conversation to:
                        </div>
                        <div className="rounded border border-border/60 bg-background px-2.5 py-2">
                          <div className="text-sm font-medium text-foreground truncate">
                            {linkPreview.recordName || `(no name) — ${linkPreview.entityId}`}
                          </div>
                          <div className="text-[11px] text-muted-foreground capitalize">
                            {linkPreview.module} · id {linkPreview.entityId}
                          </div>
                        </div>
                        {linkError && (
                          <p className="text-[11px] text-destructive">{linkError}</p>
                        )}
                        <div className="flex items-center justify-end gap-1.5 pt-1">
                          <button
                            type="button"
                            onClick={() => setLinkPreview(null)}
                            className="text-[11px] px-2 py-1 rounded hover:bg-muted text-muted-foreground"
                            disabled={linkSaving}
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            onClick={handleLinkConfirm}
                            disabled={linkSaving}
                            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                          >
                            {linkSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                            Confirm link
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Channel section — sender (number + label) + channel this
              thread is bound to. Placed after Linked record so the CRM
              context dominates the panel. Tightened paddings. */}
          <div className="px-5 py-3 border-b border-border/60">
            <div className="text-[11px] text-primary uppercase tracking-wide font-medium mb-0.5">
              <b>Channel</b>
            </div>
            <div className="flex items-center gap-2.5 rounded-md py-1.5">
              <ChannelIcon className={cn('h-4 w-4 shrink-0', channelIconColor)} />
              <div className="min-w-0 flex-1 leading-tight">
                <div className="text-[12px] font-medium text-muted-foreground truncate font-mono mb-1">
                  {senderNumber || channelLabel}
                </div>
                <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                  {activeSender?.label && <span className="truncate">{activeSender.label}</span>}
                  {activeSender?.label && senderNumber && <span className="text-muted-foreground/60">·</span>}
                  {senderNumber && <span className="truncate">{channelLabel}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="py-2">
            <PanelButton icon={<Copy className="h-4 w-4" />} onClick={onCopyPhone}>
              {conversation.channel === 'INTERCOM' ? 'Copy contact ID' : 'Copy phone number'}
            </PanelButton>
            <PanelButton
              icon={isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
              onClick={onTogglePin}
            >
              {isPinned ? 'Unpin conversation' : 'Pin conversation'}
            </PanelButton>
            <PanelButton icon={<Pencil className="h-4 w-4" />} onClick={onRename}>
              Rename conversation
            </PanelButton>
            <PanelButton icon={<Calendar className="h-4 w-4" />} onClick={onJumpToDate}>
              Jump to date
            </PanelButton>
            <PanelButton
              icon={hasReminder ? <Timer className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
              onClick={onReminder}
              accentClass={hasReminder ? 'text-amber-600 dark:text-amber-400' : undefined}
            >
              {hasReminder ? 'View reminder' : 'Set reminder'}
            </PanelButton>
          </div>

          <div className="border-t border-border/60 py-2">
            <PanelButton icon={<ArrowLeft className="h-4 w-4" />} onClick={onCloseConversation}>
              Close conversation
            </PanelButton>
          </div>

          {/* Channel routing moved up under the contact identity block. */}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PanelButton({
  icon,
  onClick,
  children,
  accentClass,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
  accentClass?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 px-5 py-2.5 text-sm text-foreground hover:bg-muted transition-colors',
        accentClass,
      )}
    >
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-left">{children}</span>
    </button>
  );
}
